import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase.ts';
import type { KitchenOrder, OrderStatus } from '../../lib/types.ts';
import { ORDER_STATUS_LABEL, FULFILLMENT_SHORT, formatDuration, formatMoney, formatSlotRange } from '../../lib/format.ts';
import { applyOptimisticStatus } from '../../hooks/useKitchenRealtime.ts';

const ACTIVE_STATUSES: OrderStatus[] = ['new', 'accepted', 'cooking', 'ready'];

const STATUS_TRANSITIONS: Record<OrderStatus, OrderStatus | null> = {
  new:       'accepted',
  accepted:  'cooking',
  cooking:   'ready',
  ready:     'completed',
  completed: null,
  cancelled: null,
  rejected:  null,
};

const STATUS_ACTION_LABEL: Record<string, string> = {
  accepted:  'Принять',
  cooking:   'Готовится',
  ready:     'Готово',
  completed: 'Выдать',
};

interface KitchenOrderCardProps {
  order: KitchenOrder;
  currency: 'RUB' | 'KZT';
  tenantSlug: string;
  locationLocalId: string;
}

export function KitchenOrderCard({ order, currency, tenantSlug, locationLocalId }: KitchenOrderCardProps) {
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const nextStatus = STATUS_TRANSITIONS[order.status];

  const updateMutation = useMutation({
    mutationFn: async ({ status, reason }: { status: OrderStatus; reason?: string }) => {
      const { data, error } = await supabase.rpc('staff_update_order_status', {
        p_order_id: order.order_id,
        p_status: status,
        p_reason: reason ?? null,
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ status }) => {
      const key = ['kitchen-orders', tenantSlug, locationLocalId];
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<KitchenOrder[]>(key);
      queryClient.setQueryData<KitchenOrder[]>(key, (old = []) =>
        applyOptimisticStatus(old, order.order_id, status),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(['kitchen-orders', tenantSlug, locationLocalId], ctx.prev);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['kitchen-orders', tenantSlug, locationLocalId] });
    },
  });

  function handleAdvance() {
    if (!nextStatus) return;
    updateMutation.mutate({ status: nextStatus });
  }

  function handleReject() {
    if (!rejectReason.trim()) return;
    updateMutation.mutate({ status: 'rejected', reason: rejectReason.trim() });
    setRejectOpen(false);
    setRejectReason('');
  }

  const isLate = order.is_late && order.status !== 'ready';

  const statusColor: Record<string, string> = {
    new:      'var(--info)',
    accepted: 'var(--accent)',
    cooking:  'var(--warning)',
    ready:    'var(--success)',
  };

  return (
    <article
      className="relative flex flex-col rounded-xl overflow-hidden"
      style={{
        background: 'var(--surface)',
        boxShadow: isLate
          ? '0 0 0 2px var(--danger), var(--shadow-md)'
          : 'var(--shadow)',
      }}
      aria-label={`Заказ №${order.order_number}`}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-3 py-2"
        style={{ background: statusColor[order.status] ?? 'var(--n-300)', color: '#fff' }}
      >
        <span className="font-bold text-base">#{order.order_number}</span>
        <span className="text-xs font-medium opacity-90">
          {ORDER_STATUS_LABEL[order.status]}
        </span>
        <span className="ml-auto text-xs opacity-80">
          {FULFILLMENT_SHORT[order.fulfillment]}
        </span>
      </div>

      <div className="flex flex-col gap-2 p-3">
        {/* Slot + wait */}
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Слот: {formatSlotRange(order.slot_start_at, order.slot_end_at, 'Asia/Novosibirsk')}
          </span>
          <span
            className="text-xs font-semibold"
            style={{ color: isLate ? 'var(--danger)' : 'var(--text-muted)' }}
          >
            {formatDuration(order.waiting_minutes)}
          </span>
        </div>

        {/* Customer */}
        {order.customer_name && (
          <p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
            {order.customer_name}
          </p>
        )}
        {order.customer_phone && (
          <a
            href={`tel:${order.customer_phone}`}
            className="text-xs"
            style={{ color: 'var(--accent)' }}
          >
            {order.customer_phone}
          </a>
        )}
        {order.delivery_address && (
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            📍 {order.delivery_address}
          </p>
        )}

        {/* Items */}
        <ul className="flex flex-col gap-1 mt-1">
          {order.items.map((item: KitchenOrder['items'][number], i: number) => (
            <li key={i} className="text-sm" style={{ color: 'var(--text)' }}>
              <span className="font-semibold">{item.quantity}×</span> {item.name}
              {item.modifiers.length > 0 && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {' '}({item.modifiers.map((m: KitchenOrder['items'][number]['modifiers'][number]) => m.name).join(', ')})
                </span>
              )}
              {item.note && (
                <span className="text-xs italic" style={{ color: 'var(--text-muted)' }}>
                  {' '}— {item.note}
                </span>
              )}
            </li>
          ))}
        </ul>

        {/* Note */}
        {order.customer_note && (
          <p
            className="rounded-lg px-2 py-1.5 text-xs"
            style={{ background: 'var(--accent-soft)', color: 'var(--text)' }}
          >
            💬 {order.customer_note}
          </p>
        )}

        {/* Total (only if visible) */}
        {order.total != null && (
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
            {formatMoney(order.total, currency)}
            {order.payment_method && (
              <span className="ml-1 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>
                {order.payment_method === 'cash_on_pickup' ? '💵' : order.payment_method === 'card_on_pickup' ? '💳' : '🌐'}
              </span>
            )}
          </p>
        )}
      </div>

      {/* Action buttons */}
      {ACTIVE_STATUSES.includes(order.status) && (
        <div className="flex gap-2 p-3 pt-0">
          {nextStatus && (
            <button
              onClick={handleAdvance}
              disabled={updateMutation.isPending}
              className="flex-1 rounded-lg py-2 text-sm font-semibold disabled:opacity-50 transition-colors"
              style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
            >
              {STATUS_ACTION_LABEL[nextStatus] ?? ORDER_STATUS_LABEL[nextStatus]}
            </button>
          )}
          {order.status === 'new' && (
            <button
              onClick={() => setRejectOpen(true)}
              disabled={updateMutation.isPending}
              className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 transition-colors"
              style={{ background: 'var(--n-100)', color: 'var(--danger)' }}
              aria-label="Отклонить заказ"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {/* Reject dialog */}
      {rejectOpen && (
        <div
          className="absolute inset-0 flex flex-col gap-3 rounded-xl p-4"
          style={{ background: 'var(--surface)', zIndex: 10 }}
          role="dialog"
          aria-label="Отклонение заказа"
        >
          <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>
            Причина отклонения
          </p>
          <textarea
            autoFocus
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            className="rounded-lg border px-3 py-2 text-sm resize-none outline-none"
            style={{
              background: 'var(--bg)',
              color: 'var(--text)',
              borderColor: 'var(--border)',
            }}
            placeholder="Например: нет ингредиентов"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={!rejectReason.trim()}
              className="flex-1 rounded-lg py-2 text-sm font-semibold disabled:opacity-40"
              style={{ background: 'var(--danger)', color: '#fff' }}
            >
              Отклонить
            </button>
            <button
              onClick={() => { setRejectOpen(false); setRejectReason(''); }}
              className="rounded-lg px-4 py-2 text-sm"
              style={{ background: 'var(--n-100)', color: 'var(--text)' }}
            >
              Назад
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
